import React, { useState } from 'react';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import { SROChatQuotaStatus } from './SRO/SROChatQuotaStatus';
import TokenAlertBanner from './TokenAlertBanner';
import { useAuth } from '../contexts/AuthContext';

export default function SROChat() {
  const { user } = useAuth();
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState('');
  const [citations, setCitations] = useState([]);
  const [tokenInfo, setTokenInfo] = useState(null);

  const ask = async (e) => {
    e?.preventDefault();
    if (!question.trim()) return toast.error('Enter a question');
    setLoading(true);
    setAnswer('');
    setCitations([]);
    try {
      const res = await api.sro.chat({ question, topK: 5 });
      setAnswer(res.data?.answer || '');
      setCitations(res.data?.citations || []);
      setTokenInfo(res.data?.tokenInfo || null);
      
      // Show token usage info
      if (res.data?.tokenInfo) {
        toast.success(`Used ${res.data.tokenInfo.tokensUsed} tokens. ${res.data.tokenInfo.tokensRemaining} remaining.`);
      }
    } catch (err) {
      const errorData = err?.response?.data;
      if (errorData?.quotaExceeded) {
        toast.error('Your token quota has been exceeded. Please upgrade your plan.');
      } else if (errorData?.error === 'SRO Chat disabled') {
        toast.error(`SRO Chat disabled: ${errorData?.message}`);
      } else {
        toast.error(errorData?.error || 'Chat failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4">
      {/* Token Alert Banner */}
      {user?.id && <TokenAlertBanner userId={user.id} />}

      <h2 className="text-xl font-semibold mb-3">SRO Knowledge Chat</h2>
      
      {/* Quota Status Card */}
      <div className="mb-4">
        <SROChatQuotaStatus userId={user?.id} showDetails={false} />
      </div>

      <form onSubmit={ask} className="flex gap-2 mb-4">
        <input
          className="flex-1 border rounded px-3 py-2"
          placeholder="Ask about sales tax rules per SROs / official FBR site..."
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          disabled={loading}
        />
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
          disabled={loading}
        >
          {loading ? 'Asking…' : 'Ask'}
        </button>
      </form>

      {answer && (
        <div className="mb-4 p-3 border rounded bg-gray-50 whitespace-pre-wrap">
          <div className="text-sm font-semibold mb-2 text-gray-700">Answer:</div>
          {answer}
          
          {tokenInfo && (
            <div className="mt-3 pt-3 border-t border-gray-300 text-xs text-gray-600">
              <div>✓ Used {tokenInfo.tokensUsed} tokens</div>
              <div>✓ {tokenInfo.tokensRemaining.toLocaleString()} remaining of {tokenInfo.monthlyAllowance.toLocaleString()}</div>
              <div>✓ Resets: {new Date(tokenInfo.quotaResetDate).toLocaleDateString()}</div>
            </div>
          )}
        </div>
      )}

      <div>
        <div className="font-medium mb-2">Citations</div>
        {citations.length === 0 && <div className="text-sm text-gray-600">No citations</div>}
        <ul className="space-y-2">
          {citations.map((c, i) => (
            <li key={i} className="text-sm">
              {c.source_url ? (
                <>
                  [FBR URL] <a className="text-blue-600 underline" href={c.source_url} target="_blank" rel="noreferrer">{c.source_url}</a>
                </>
              ) : (
                <>
                  [SRO] {c.filename} — chunk {c.chunk_index}{typeof c.page_number === 'number' ? `, page ${c.page_number}` : ''}
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
